import { useState } from "react";
import { styled, keyframes } from "@mui/system";
import TextSimple from "@/src-refactor/modules/shared/components/typography/text-simple/TextSimple";
import Alert from "@icons-rf/timeline/alertIcon.svg?react";
import AIcon from "@icons-rf/timeline/Aicon.svg?react";
import AIcon2 from "@icons-rf/timeline/Aicon2.svg?react";
import { Tooltip } from "@mui/material";
import useTimeLineData from "./hooks/useTimeLineData";
import { TimeLineDescriptionData } from "./const/time-line-description";

export interface HistoryTimelineProps {
  zIndex?: number;
  monthCircle?: number;
  month?: string;
  color?: string;
  icono?: any;
  onDescriptionClick?: (description: {
    title: string;
    descriptions: string;
    month: string;
    color: string;
    icono: any;
  }) => void;
}

const HistoryTimeline = ({ onDescriptionClick, month }: HistoryTimelineProps) => {
  const { timeLineData } = useTimeLineData();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

  const handleClickDescription = (id: number) => {
    const { title, description } = TimeLineDescriptionData[id];
    if (onDescriptionClick) {
      onDescriptionClick({
        title,
        descriptions: description,
        month: timeLineData[id].month,
        color: timeLineData[id].color,
        icono: timeLineData[id].icon
      });
    }
    setSelectedItemIndex(id);
  };

  return (
    <TimelineContainer>
      <TimelineContent>
        {timeLineData.map((item, index) => (
          <TimelineItem
            key={item.month}
            style={{ backgroundColor: item.color }}
            zIndex={timeLineData.length - index}
            onClick={() => handleClickDescription(index)}
          >
            <ArrowEnd style={{ borderLeft: `15px solid ${item.color}` }} />
            <Circle style={{ backgroundColor: item.color }}>
              <InnerCircle
                monthCircle={item.id}
                className={item.id === 6 ? "neon-border" : ""}
              >
                <IconStyles>{item.icon}</IconStyles>
              </InnerCircle>
            </Circle>
            <BubbleContent odd={index % 2 === 0}>
              <ContentYear style={{ backgroundColor: item.color }}>
                {`Mes ${item.month}`}

                {[2, 8, 9, 12, 14, 10].includes(item.id) && (
                  <Tooltip title="Detiene comercialización" placement="top" arrow>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Alert width={16} height={16} />
                    </div>
                  </Tooltip>
                )}

                {[15, 10].includes(item.id) && (
                  <Tooltip title="Si aplica" placement="top" arrow>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <AIcon width={16} height={16} />
                    </div>
                  </Tooltip>
                )}
              </ContentYear>

              <Content>
                {item.id !== 5 && item.id !== 6 && (
                  <TextSimple color="blue" size="description" weight="500">
                    {item.content}
                  </TextSimple>
                )}

                {item.id === 6 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingTop: "0.8rem",
                      justifyContent: "center"
                    }}
                  >
                    <TextSimple
                      sx={{ display: "flex", justifyContent: "center" }}
                      color="blue"
                      size="sub-title"
                      weight="700"
                    >
                      {item.content}
                    </TextSimple>
                  </div>
                )}

                {item.id === 5 && (
                  <div>
                    <Span>• Estatus jurídico</Span>

                    <Tooltip title="Si aplica" placement="top" arrow>
                      <Span>
                        • Previo MITUR <AIcon2 width={16} height={16} />
                      </Span>
                    </Tooltip>
                  </div>
                )}
              </Content>
            </BubbleContent>
            <GreyLine odd={index % 2 === 0} />
          </TimelineItem>
        ))}
      </TimelineContent>
    </TimelineContainer>
  );
};

export default HistoryTimeline;

const Span = styled("span")`
  display: block;
  font-size: 0.8rem;
  color: #1a3668;
  font-weight: 500;
  text-align: start;
  padding-left: 1rem;
  align-items: center;
  display: flex;
  gap: 0.2rem;
`;

const TimelineContent = styled("div")`
  --color1: #3e5a8b;
  --color2: #1a3668;
  --color3: #2a2f36;
  --color4: #1a3668;
  --color5: #1a3668;
  --color6: #3861a8;
  --color7: #3e506f;
  padding: 200px 0;
  font-family: "Inter";
  display: inline-flex;
  min-width: max-content;
  padding-left: 1.2rem;
`;

const TimelineItem = styled("div")<HistoryTimelineProps>`
  position: relative;
  width: 150px;
  height: 30px;
  display: inline-table;
  z-index: ${({ zIndex }) => zIndex};
  position: relative;
  cursor: pointer;

  &:first-of-type {
    left: 0;
  }
`;

const ArrowEnd = styled("div")`
  position: absolute;
  right: -14px;
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
`;

const Circle = styled("div")<HistoryTimelineProps>`
  position: absolute;
  background-position: center center;
  border-radius: 25px;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  width: 50px;
  height: 50px;

  &:first-of-type {
    left: 0;
  }
`;

const neonGlow = keyframes`
    0% {
        border-color: #B22B2E;
        box-shadow: 0 2px 2px #B22B2E, 0 0 5px #B22B2E, 0 0 8px #B22B2E, 0 0 10px #B22B2E, 0 0 20px #B22B2E, 0 0 30px #B22B2E;
    }
    25% {
        border-color: #1A3668;
        box-shadow: 0 2px 2px #1A3668, 0 0 5px #1A3668, 0 0 8px #1A3668, 0 0 10px #1A3668, 0 0 20px #1A3668, 0 0 30px #1A3668;
    }
    50% {
        border-color: #007DC3;
        box-shadow: 0 2px 2px #007DC3, 0 0 5px #007DC3, 0 0 8px #007DC3, 0 0 10px #007DC3, 0 0 20px #007DC3, 0 0 30px #007DC3;
    }
    75% {
        border-color: #3E5A8B;
        box-shadow: 0 2px 2px #3E5A8B, 0 0 5px #3E5A8B, 0 0 8px #3E5A8B, 0 0 10px #3E5A8B, 0 0 20px #3E5A8B, 0 0 30px #3E5A8B;
    }
    100% {
        border-color: #B22B2E;
        box-shadow: 0 2px 2px #B22B2E, 0 0 5px #B22B2E, 0 0 8px #B22B2E, 0 0 10px #B22B2E, 0 0 20px #B22B2E, 0 0 30px #B22B2E;
    }
`;

const InnerCircle = styled("div")<{ monthCircle: number }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: #fff;
  border: solid 1px #404041;
  background-position: center center;
  background-size: 30px 30px;
  background-color: #fff;

  &.neon-border {
    animation: ${neonGlow} 3s infinite linear;
    border: solid 1px #f1f1f1;
  }
`;

const BubbleContent = styled("div")`
  overflow: hidden;
  text-align: center;
  position: absolute;
  background-color: #fff;
  width: 165px;

  height: 100px;
  border: solid 1px #4d4d4d;
  transition: transform 0.3s;

  transition: ease-in-out 0.3s;

  &:hover {
    transform: scale(1.1);
  }

  &:first-of-type {
    left: -75px;
  }

  ${({ odd }: { odd: any }) =>
    odd
      ? `
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        border-bottom-left-radius: 20px;
        border-bottom-right-radius: 20px;
        top: -150px;
    `
      : `
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        bottom: -160px;
    `}
`;

const GreyLine = styled("div")<{ odd: any }>`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 31px;
  background-color: #292929;

  &:first-of-type {
    left: 0;
  }

  ${({ odd }: { odd: any }) =>
    odd ? "top: -35px; left: 50%;" : "bottom: -88px;  left: 50%; height: 46px;"}
`;

const ContentYear = styled("div")`
  padding: 5px;
  color: #fff;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
`;

const TimelineContainer = styled("div")`
  max-width: 87dvw;
  overflow: hidden;
  overflow-x: scroll;
  z-index: 0;

  @media (max-width: 768px) {
    max-width: 80vw;
  }
`;

const IconStyles = styled("div")`
  display: flex;
  align-items: center;
  color: #1a3668;
`;

const Text = styled("div")`
  display: flex;
  align-items: center;
  color: #1a3668;
  justify-content: center;
  font-size: 12px;
  font-family: "Inter";
  padding: 2px 5px;
  white-space: pre-line;
`;

const Content = styled("div")`
  margin: auto 0;
  padding: 5px;
  color: #000;
  font-size: 14px;
  height: 60px;
  display: block;
  position: relative;
`;
